import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  limit,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { useAuth } from "./auth/AuthContext";
import { auth, db, call, ensureAnon, storage } from "./firebase";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import MyOddventureGames from "./MyOddventureGames";

const HEX_RADIUS = 3;
const HEX_SIZE = 30; // legacy (used for bounds) — canvas uses smaller sizes locally
const HEX_SPACING = 6;
const HEX_CENTER = HEX_SIZE + HEX_SPACING;
const HEX_W = HEX_SIZE * 2;
const HEX_H = Math.sqrt(3) * HEX_SIZE;

const CLASS_OPTIONS = [
  { id: "fighter", name: "Fighter", hitDie: 12, skills: ["Athletics", "Intimidation", "Survival", "Perception"] },
  { id: "rogue", name: "Rogue", hitDie: 8, skills: ["Acrobatics", "Stealth", "Sleight of Hand", "Perception", "Insight"] },
  { id: "wizard", name: "Wizard", hitDie: 6, skills: ["Arcana", "History", "Investigation", "Insight", "Medicine"] },
  { id: "cleric", name: "Cleric", hitDie: 8, skills: ["Insight", "Medicine", "Religion", "History"] },
  { id: "ranger", name: "Ranger", hitDie: 10, skills: ["Nature", "Animal Handling", "Survival", "Perception", "Stealth"] },
  { id: "bard", name: "Bard", hitDie: 8, skills: ["Persuasion", "Performance", "Insight", "Deception", "History"] },
];
const SKILLS_REQUIRED = {
  fighter: 2,
  rogue: 3,
  wizard: 2,
  cleric: 2,
  ranger: 2,
  bard: 3,
};

const BIOMES = ["Blazing Steppe", "Mistwood", "Glass Marsh", "Thunder Mesa", "Echoing Chasm", "Aurora Tundra", "Sunken Archive"];
const FEATURES = ["Ancient obelisk", "Broken bridge", "Hexed shrine", "Shimmering well", "Signal tower", "Forgotten campsite", "Glowroot grove"];
const DANGERS = ["bandit outriders", "wyvern on the wind", "haunting mirage", "clockwork sentries", "arcane storm", "rotting leviathan", "silent cultists"];
const DELIGHTS = ["a friendly tinkerer", "veils of soft moss", "a cache of moon-lit coins", "talking foxes", "a floating lantern", "hot tea and embers", "singing stones"];

const NEIGHBORS = [
  { q: 1, r: 0 },
  { q: 0, r: 1 },
  { q: -1, r: 1 },
  { q: -1, r: 0 },
  { q: 0, r: -1 },
  { q: 1, r: -1 },
];

function mulberry32(a) {
  return function () {
    let t = a += 0x6D2B79F5;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick(rand, arr) {
  return arr[Math.floor(rand() * arr.length)];
}

function abilityMod(score) {
  return Math.floor((score - 10) / 2);
}

function abilityScores() {
  // 4d6 drop lowest, six times
  const rollOne = () => {
    const dice = [0, 0, 0, 0].map(() => 1 + Math.floor(Math.random() * 6)).sort((a, b) => a - b);
    return dice.slice(1).reduce((a, b) => a + b, 0);
  };
  return Array.from({ length: 6 }, rollOne);
}

function levelFromXp(xp = 0) {
  const thresholds = [0, 300, 900, 2700, 6500, 14000, 23000, 34000, 48000, 64000, 85000, 100000, 120000, 140000, 165000, 195000, 225000, 265000, 305000, 355000];
  let level = 1;
  let next = thresholds[1];
  for (let i = 1; i < thresholds.length; i++) {
    if (xp >= thresholds[i]) {
      level = i + 1;
      next = thresholds[i + 1] || null;
    }
  }
  return { level, nextXp: next };
}

function rollExpression(expr) {
  const match = String(expr || "").trim().match(/^([1-9]\\d*)d([1-9]\\d*)([+-]\\d+)?$/i);
  if (!match) throw new Error("Use NdM or NdM+K (e.g., 1d20, 2d6+3)");
  const count = Number(match[1]);
  const sides = Number(match[2]);
  const mod = match[3] ? Number(match[3]) : 0;
  const rolls = Array.from({ length: count }, () => 1 + Math.floor(Math.random() * sides));
  const total = rolls.reduce((a, b) => a + b, 0) + mod;
  return { total, rolls, mod, sides, count };
}

function hexId(q, r) {
  return `${q},${r}`;
}

function hexDistance(a, b) {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  const ds = -a.q - a.r - (-b.q - b.r);
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(ds)) / 2;
}

function axialToPixel(q, r) {
  return {
    // flat-top axial to pixel with spacing
    x: HEX_CENTER * 1.5 * q,
    y: HEX_CENTER * Math.sqrt(3) * (r + q / 2),
  };
}

function generateWorld(seed, radius = HEX_RADIUS) {
  const rand = mulberry32(seed || 1);
  const cells = [];
  for (let q = -radius; q <= radius; q++) {
    const r1 = Math.max(-radius, -q - radius);
    const r2 = Math.min(radius, -q + radius);
    for (let r = r1; r <= r2; r++) {
      const cell = {
        id: hexId(q, r),
        q,
        r,
        biome: pick(rand, BIOMES),
        feature: pick(rand, FEATURES),
        danger: pick(rand, DANGERS),
        delight: pick(rand, DELIGHTS),
      };
      cells.push(cell);
    }
  }
  return cells;
}

function nextColor() {
  const palette = ["#ff8a5b", "#81f0c3", "#8fd1ff", "#f7d66b", "#e18eff", "#9ef7ff", "#ffa9c2"];
  return palette[Math.floor(Math.random() * palette.length)];
}

function shortId() {
  return Math.random().toString(16).slice(2, 8);
}

function getMaxSkillsForClass(classId) {
  return SKILLS_REQUIRED[classId] || 2;
}

function Box({ title, children, tone = "default" }) {
  const tones = {
    default: "rgba(255,255,255,0.05)",
    ember: "linear-gradient(145deg, rgba(255,170,136,0.16), rgba(72,38,28,0.4))",
    frost: "linear-gradient(135deg, rgba(143,211,255,0.16), rgba(24,46,63,0.5))",
    moss: "linear-gradient(135deg, rgba(129,240,195,0.14), rgba(23,63,46,0.45))",
  };
  return (
    <div style={{
      background: tones[tone] || tones.default,
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 14,
      padding: "14px 16px",
      boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
      backdropFilter: "blur(4px)",
    }}>
      {title && <div style={{ fontWeight: 700, marginBottom: 8, letterSpacing: 0.5 }}>{title}</div>}
      {children}
    </div>
  );
}

export default function Oddventure() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { tableId: routeTableId } = useParams();
  const isDetail = Boolean(routeTableId);
  const [activeTableId, setActiveTableId] = useState(routeTableId || "");
  const [tableIdInput, setTableIdInput] = useState(routeTableId || "");
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [dmInput, setDmInput] = useState("");
  const [dmReply, setDmReply] = useState("");
  const [dmLoading, setDmLoading] = useState(false);
  const [rollExpr, setRollExpr] = useState("1d20");
  const [rolling, setRolling] = useState(false);

  const [charName, setCharName] = useState("");
  const [charClass, setCharClass] = useState("fighter");
  const [charSkills, setCharSkills] = useState([]);
  const [charNote, setCharNote] = useState("");
  const [charStats, setCharStats] = useState([]);
  const [accent, setAccent] = useState(nextColor());
  const [charAC, setCharAC] = useState(10);
  const [charHP, setCharHP] = useState(8);
  const [charSpeed, setCharSpeed] = useState(30);
  const [charXP, setCharXP] = useState(0);
  const [charAvatar, setCharAvatar] = useState("");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarDraft, setAvatarDraft] = useState(null); // {url,width,height,name,file}
  const [avatarZoom, setAvatarZoom] = useState(1);
  const [avatarOffsetX, setAvatarOffsetX] = useState(0.5);
  const [avatarOffsetY, setAvatarOffsetY] = useState(0.5);
  const [editingCharacterId, setEditingCharacterId] = useState(null);
  const avatarInputRef = useRef(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [dmMessages, setDmMessages] = useState([]);
  const chatCol = useMemo(() => (activeTableId ? collection(db, "oddventure_tables", activeTableId, "chat") : null), [activeTableId]);
  const dmCol = useMemo(() => (activeTableId ? collection(db, "oddventure_tables", activeTableId, "dm_log") : null), [activeTableId]);
  const classMeta = useMemo(() => CLASS_OPTIONS.find((c) => c.id === charClass), [charClass]);
  const recentDmHistory = useMemo(() => {
    const trimmed = dmMessages.slice(0, 12);
    return [...trimmed].reverse(); // oldest first
  }, [dmMessages]);
  const ensureChatAuth = async () => {
    const cur = auth.currentUser;
    if (!cur || cur.isAnonymous) throw new Error("signin");
    await cur.getIdToken(true);
    return cur;
  };
  const showMaps = false; // temporarily hide maps while layout is tuned

  // Load draft character only
  useEffect(() => {
    const draft = localStorage.getItem("oddventure:draftCharacter");
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        setCharName(parsed.name || "");
        setCharClass(parsed.classId || "fighter");
        setCharSkills(parsed.skills || []);
        setCharNote(parsed.note || "");
        setCharStats(Array.isArray(parsed.stats) ? parsed.stats : []);
        setAccent(parsed.accent || nextColor());
      } catch (err) {
        console.warn("[Oddventure] could not load draft", err);
      }
    }
  }, []);

  // Reset state based on route (list vs detail)
  useEffect(() => {
    if (isDetail) {
      setActiveTableId(routeTableId || "");
      setTableIdInput(routeTableId || "");
    } else {
      setActiveTableId("");
      setTable(null);
    }
  }, [isDetail, routeTableId]);

  useEffect(() => {
    const draft = {
      name: charName,
      classId: charClass,
      skills: charSkills,
      note: charNote,
      stats: charStats,
      accent,
    };
    localStorage.setItem("oddventure:draftCharacter", JSON.stringify(draft));
  }, [charName, charClass, charSkills, charNote, charStats, accent]);

  // Group chat (table-scoped)
  useEffect(() => {
    if (!chatCol) return;
    const qy = query(chatCol, orderBy("createdAt", "desc"), limit(50));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null };
        });
        setChatMessages(rows);
      },
      (err) => console.error("[chat]", err)
    );
    return () => unsub();
  }, [chatCol]);

  // DM chat history (table-scoped)
  useEffect(() => {
    if (!dmCol) return;
    const qy = query(dmCol, orderBy("createdAt", "desc"), limit(200));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null };
        });
        setDmMessages(rows);
      },
      (err) => console.error("[dm log]", err)
    );
    return () => unsub();
  }, [dmCol]);

  useEffect(() => {
    if (!activeTableId) {
      setTable(null);
      return undefined;
    }
    setLoading(true);
    const ref = doc(db, "oddventure_tables", activeTableId);
    const unsub = onSnapshot(ref, (snap) => {
      setLoading(false);
      setTable(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    return () => unsub();
  }, [activeTableId]);

  async function createTable() {
    try {
      setStatus("Forging a fresh table...");
      const seed = Math.floor(Math.random() * 1_000_000);
      const cells = generateWorld(seed, HEX_RADIUS);
      const starting = hexId(0, 0);
      const revealed = new Set([starting]);
      NEIGHBORS.map(n => hexId(n.q, n.r)).forEach(id => revealed.add(id));
      const ownerId = user?.uid || "anon";
      const ownerName = user?.displayName || "Oddmaster";

      const docRef = await addDoc(collection(db, "oddventure_tables"), {
        name: "Chadventure Table",
        code: `OV-${shortId().toUpperCase()}`,
        createdAt: serverTimestamp(),
        ownerId,
        ownerName,
        seed,
        cells,
        partyPosition: { q: 0, r: 0 },
        discovered: Array.from(revealed),
        characters: [],
        seated: [],
        dmMood: "Candlelit curiosity",
        log: [{
          text: "A silver quill hovers in the air. The Chadmaster invites heroes to sit.",
          by: "Chadmaster",
          at: Date.now(),
        }],
      });
      setActiveTableId(docRef.id);
      setTableIdInput(docRef.id);
      navigate(`/oddventure/${docRef.id}`);
      seedChadIntro(docRef.id);
      setStatus("Table conjured.");
    } catch (err) {
      console.error(err);
      setStatus("Could not create table. Check Firebase config.");
    }
  }

  async function joinTableById() {
    if (!tableIdInput) return;
    setActiveTableId(tableIdInput.trim());
    navigate(`/oddventure/${tableIdInput.trim()}`);
  }

  function toggleSkill(skill) {
    const limit = getMaxSkillsForClass(charClass);
    if (charSkills.includes(skill)) {
      setCharSkills((prev) => prev.filter((s) => s !== skill));
    } else {
      if (charSkills.length >= limit) return;
      setCharSkills((prev) => [...prev, skill]);
    }
  }

  function rollAbilities() {
    setCharStats(abilityScores());
  }


  async function forgeCharacter() {
    if (!activeTableId) {
      setStatus("Join or create a table first.");
      return;
    }
    if (!charName.trim()) {
      setStatus("Name your character.");
      return;
    }
    const limit = getMaxSkillsForClass(charClass);
    if (charSkills.length !== limit) {
      setStatus(`Choose ${limit} trained skills.`);
      return;
    }
    if (charStats.length !== 6) {
      setStatus("Roll ability scores first.");
      return;
    }

    const classMeta = CLASS_OPTIONS.find((c) => c.id === charClass);
    const ownerName = user?.displayName || "Wanderer";
    const ownerId = user?.uid || "anon";

    try {
      const ref = doc(db, "oddventure_tables", activeTableId);
      let savedChar = null;
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error("Table missing.");
        const data = snap.data();
        const characters = data.characters || [];
        const conMod = abilityMod(charStats[2] || 10);
        const dexMod = abilityMod(charStats[1] || 10);
        const derivedHp = Math.max((classMeta?.hitDie || 8) + conMod, 1);
        const hp = charHP || derivedHp;
        const ac = charAC || 10 + dexMod;
        const speed = charSpeed || 30;
        const newChar = {
          id: editingCharacterId || myCharacter?.id || shortId(),
          name: charName.trim(),
          classId: charClass,
          className: classMeta?.name || "Adventurer",
          skills: charSkills,
          stats: charStats,
          hp,
          ac,
          speed,
          xp: Number(charXP) || 0,
          avatarUrl: charAvatar || myCharacter?.avatarUrl || "",
          avatarCrop: {
            zoom: avatarZoom,
            offsetX: avatarOffsetX,
            offsetY: avatarOffsetY,
          },
          note: charNote.trim(),
          color: accent,
          ownerId,
          ownerName,
          createdAt: Date.now(),
        };
        savedChar = newChar;
        const others = characters.filter(
          (c) => c.id !== newChar.id && c.ownerId !== ownerId
        );
        const log = [{
          text: `${newChar.name} arrives as a ${newChar.className}.`,
          by: ownerName,
          at: Date.now(),
        }, ...(data.log || [])].slice(0, 40);
        // Update only the fields we need to avoid overwriting other concurrent changes.
        tx.update(ref, {
          characters: [...others, newChar],
          log,
        });
      });
      setStatus(editingCharacterId || myCharacter ? "Character updated." : "Character forged.");
      setCharName("");
      setCharSkills([]);
      setCharNote("");
      setCharStats([]);
      setCharAC(10);
      setCharHP(8);
      setCharSpeed(30);
      setCharXP(0);
      setAccent(nextColor());
      setEditingCharacterId(null);
      // Optimistically update local table so roster reflects latest character/avatar immediately
      if (savedChar) {
        setTable((prev) => {
          if (!prev || prev.id !== activeTableId) return prev;
          const characters = (prev.characters || []).filter((c) => c.id !== savedChar.id && c.ownerId !== ownerId);
          return { ...prev, characters: [...characters, savedChar] };
        });
      }
    } catch (err) {
      console.error(err);
      setStatus(`Could not save character: ${err.message || err.toString()}`);
    }
  }

  const beginEdit = () => {
    if (!myCharacter) return;
    setEditingCharacterId(myCharacter.id);
    setCharName(myCharacter.name || "");
    setCharClass(myCharacter.classId || "fighter");
    setCharSkills(myCharacter.skills || []);
    setCharNote(myCharacter.note || "");
    setCharStats(Array.isArray(myCharacter.stats) ? myCharacter.stats : []);
    setAccent(myCharacter.color || nextColor());
    setCharAC(myCharacter.ac ?? 10);
    setCharHP(myCharacter.hp ?? 8);
    setCharSpeed(myCharacter.speed ?? 30);
    setCharXP(myCharacter.xp ?? 0);
    setCharAvatar(myCharacter.avatarUrl || "");
  };

  async function sitAtTable(characterId) {
    if (!activeTableId || !table) return;
    const character = (table.characters || []).find((c) => c.id === characterId);
    if (!character) return;
    const ownerId = user?.uid || "anon";
    const ownerName = user?.displayName || "Traveler";
    try {
      const ref = doc(db, "oddventure_tables", activeTableId);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error("Missing table");
        const data = snap.data();
        const seated = data.seated || [];
        const next = seated.filter((s) => s.ownerId !== ownerId);
        next.unshift({
          ownerId,
          ownerName,
          characterId,
          characterName: character.name,
          at: Date.now(),
        });
        const log = [{
          text: `${ownerName} sits as ${character.name}.`,
          by: "Table",
          at: Date.now(),
        }, ...(data.log || [])].slice(0, 40);
        tx.update(ref, { seated: next.slice(0, 10), log });
      });
      setStatus(`You take the role of ${character.name}.`);
    } catch (err) {
      console.error(err);
      setStatus("Could not sit at the table.");
    }
  }

  const askOddmaster = async () => {
    if (!dmInput.trim()) return;
    if (!activeTableId) {
      setStatus("Join or create a table first.");
      return;
    }
    try {
      await ensureChatAuth();
    } catch (err) {
      setStatus("Sign in to chat with The Chadmaster.");
      return;
    }
    setDmLoading(true);
    setStatus("");
    try {
      const payload = {
        tableId: activeTableId,
        tableSnapshot: {
          characters: table?.characters || [],
          log: table?.log || [],
          partyPosition: table?.partyPosition,
          seated: table?.seated || [],
          dmHistory: recentDmHistory,
        },
        message: dmInput.trim(),
      };
      console.log("[askOddmaster] request", payload);
      const res = await call("oddventureDm", payload);
      console.log("[askOddmaster] response", res?.data);
      if (dmCol) {
        await addDoc(dmCol, {
          role: "user",
          text: dmInput.trim(),
          createdAt: serverTimestamp(),
          uid: user?.uid || "anon",
        });
        await addDoc(dmCol, {
          role: "assistant",
          text: res.data?.reply || "No reply.",
          createdAt: serverTimestamp(),
          uid: "chadmaster",
        });
      }
      setDmReply(res.data?.reply || "No reply.");
      setDmInput("");
    } catch (err) {
      console.error("[askOddmaster] error", err);
      setStatus(`Chad call failed: ${err.message || err.toString()}`);
    } finally {
      setDmLoading(false);
    }
  }

  const rollDiceForTable = async (exprOverride) => {
    const expr = (exprOverride || rollExpr || "").trim();
    if (!expr) return;
    if (!activeTableId) {
      setStatus("Join or create a table first.");
      return;
    }
    let roll;
    try {
      roll = rollExpression(expr);
    } catch (err) {
      setStatus(err.message || "Bad roll expression");
      return;
    }
    try {
      await ensureChatAuth();
    } catch (err) {
      setStatus("Sign in to roll dice.");
      return;
    }
    setRolling(true);
    const seat = (table?.seated || []).find((s) => s.ownerId === user?.uid);
    const actor = seat?.characterName || user?.displayName || "Traveler";
    const detail = `${roll.count}d${roll.sides}${roll.mod ? (roll.mod > 0 ? `+${roll.mod}` : roll.mod) : ""}`;
    const breakdown = `${roll.rolls.join(" + ")}${roll.mod ? (roll.mod > 0 ? ` + ${roll.mod}` : ` - ${Math.abs(roll.mod)}`) : ""}`;
    const text = `${actor} rolls ${detail}: ${roll.total} (${breakdown})`;
    try {
      if (dmCol) {
        await addDoc(dmCol, {
          role: "user",
          text,
          createdAt: serverTimestamp(),
          uid: user?.uid || "anon",
          roll: { expr: detail, total: roll.total, rolls: roll.rolls, mod: roll.mod },
          characterName: seat?.characterName || null,
        });
      }
      setStatus(`Rolled ${detail} = ${roll.total}`);
    } catch (err) {
      console.error("[rollDiceForTable]", err);
      setStatus(`Roll failed: ${err.message || err.toString()}`);
    } finally {
      setRolling(false);
    }
  }

  const handleAvatarUpload = async (file) => {
    if (!file) return;
    try {
      const objectUrl = URL.createObjectURL(file);
      const bitmap = await createImageBitmap(file);
      setAvatarDraft({ url: objectUrl, width: bitmap.width, height: bitmap.height, name: file.name, file });
      setAvatarZoom(1);
      setAvatarOffsetX(0.5);
      setAvatarOffsetY(0.5);
      setStatus("Adjust your avatar crop, then save.");
    } catch (err) {
      console.error("[avatar load]", err);
      setStatus("Could not load avatar for cropping.");
    }
  };

  const handleAvatarClick = () => {
    avatarInputRef.current?.click();
  };

  const saveAvatarCrop = async () => {
    if (!avatarDraft || !activeTableId) return;
    setAvatarUploading(true);
    try {
      const img = new Image();
      img.src = avatarDraft.url;
      await img.decode();
      const baseSize = Math.min(img.naturalWidth || img.width, img.naturalHeight || img.height);
      const outputSize = 512; // crop down for storage savings
      const canvas = document.createElement("canvas");
      canvas.width = outputSize;
      canvas.height = outputSize;
      const ctx = canvas.getContext("2d");
      const cropSize = baseSize / Math.max(avatarZoom, 0.5);
      const maxX = Math.max((img.naturalWidth || img.width) - cropSize, 0);
      const maxY = Math.max((img.naturalHeight || img.height) - cropSize, 0);
      const sx = Math.min(Math.max(((img.naturalWidth || img.width) - cropSize) * avatarOffsetX, 0), maxX);
      const sy = Math.min(Math.max(((img.naturalHeight || img.height) - cropSize) * avatarOffsetY, 0), maxY);
      ctx.clearRect(0, 0, outputSize, outputSize);
      ctx.drawImage(img, sx, sy, cropSize, cropSize, 0, 0, outputSize, outputSize);
      let blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not crop image"))), "image/jpeg", 0.92);
      }).catch(() => null);
      if (!blob) blob = avatarDraft.file;
      await ensureChatAuth();
      const charId = editingCharacterId || myCharacter?.id || "temp";
      const path = `avatars/${user?.uid || "anon"}/${charId}.jpg`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, blob, { contentType: "image/jpeg" });
      const url = await getDownloadURL(ref);
      setCharAvatar(url);
      setAvatarDraft(null);
      // persist crop params too
      // Persist avatar to the character in the table immediately when possible.
      if (myCharacter) {
        try {
          const docRef = doc(db, "oddventure_tables", activeTableId);
          await runTransaction(db, async (tx) => {
            const snap = await tx.get(docRef);
            if (!snap.exists()) return;
            const data = snap.data();
            const characters = data.characters || [];
            const next = characters.map((c) =>
              c.ownerId === (user?.uid || "anon")
                ? {
                    ...c,
                    avatarUrl: url,
                    avatarCrop: { zoom: avatarZoom, offsetX: avatarOffsetX, offsetY: avatarOffsetY },
                  }
                : c
            );
            tx.update(docRef, { characters: next });
          });
          // Optimistically update local table state so roster refreshes immediately
          setTable((prev) => {
        if (!prev || prev.id !== activeTableId) return prev;
        const characters = (prev.characters || []).map((c) =>
          c.ownerId === (user?.uid || "anon")
            ? { ...c, avatarUrl: url, avatarCrop: { zoom: avatarZoom, offsetX: avatarOffsetX, offsetY: avatarOffsetY } }
            : c
        );
        return { ...prev, characters };
      });
        } catch (err) {
          console.error("[avatar persist]", err);
        }
      }
      setStatus("Avatar uploaded.");
    } catch (err) {
      console.error("[avatar upload]", err);
      setStatus(`Avatar upload failed: ${err.message || err.toString()}`);
    } finally {
      setAvatarUploading(false);
    }
  };

  async function seedChadIntro(tableId) {
    try {
      await ensureAnon();
      const introPrompt = "Set the opening scene for a hex-crawl party arriving at camp. 60-90 words, end with a question.";
      const res = await call("oddventureDm", {
        tableId,
        message: introPrompt,
        tableSnapshot: {},
      });
      const intro = res.data?.reply || "Lanterns gutter as the fog parts. What do you do?";
      const col = collection(db, "oddventure_tables", tableId, "dm_log");
      await addDoc(col, {
        role: "assistant",
        text: intro,
        createdAt: serverTimestamp(),
        uid: "chadmaster",
      });
    } catch (err) {
      console.error("[seedChadIntro]", err);
    }
  }

  async function moveParty(targetId) {
    if (!table || !activeTableId) return;
    const target = (table.cells || []).find((c) => c.id === targetId);
    if (!target) return;
    try {
      const ref = doc(db, "oddventure_tables", activeTableId);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error("Missing table");
        const data = snap.data();
        const current = data.partyPosition || { q: 0, r: 0 };
        if (hexDistance(current, target) !== 1) throw new Error("Move 1 hex at a time.");
        const discovered = new Set(data.discovered || []);
        discovered.add(target.id);
        NEIGHBORS.forEach((n) => discovered.add(hexId(target.q + n.q, target.r + n.r)));
        const log = [{
          text: `The party enters ${target.biome}, approaching a ${target.feature}. Beware of ${target.danger}.`,
          by: user?.displayName || "Oddmaster",
          at: Date.now(),
        }, ...(data.log || [])].slice(0, 40);
        tx.update(ref, {
          partyPosition: { q: target.q, r: target.r },
          discovered: Array.from(discovered),
          log,
        });
      });
      setStatus(`Moved to ${target.biome}.`);
    } catch (err) {
      console.error(err);
      setStatus("Could not move party.");
    }
  }

  const discovered = useMemo(() => new Set(table?.discovered || []), [table]);
  const partyPosition = useMemo(
    () => table?.partyPosition || { q: 0, r: 0 },
    [table]
  );
  const myCharacter = useMemo(() => {
    if (!table || !user) return null;
    return (table.characters || []).find((c) => c.ownerId === user.uid);
  }, [table, user]);
  const savedAvatar = useMemo(() => myCharacter?.avatarUrl || "", [myCharacter]);
  const avatarDisplay = useMemo(
    () => charAvatar || avatarDraft?.url || savedAvatar,
    [charAvatar, avatarDraft, savedAvatar]
  );
  const isEditing = useMemo(() => Boolean(editingCharacterId || !myCharacter), [editingCharacterId, myCharacter]);
  const [encounterMap, setEncounterMap] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  // Hydrate form from saved character when present (always editing view)
  useEffect(() => {
    if (myCharacter && !editingCharacterId) {
      setCharName(myCharacter.name || "");
      setCharClass(myCharacter.classId || "fighter");
      setCharSkills(myCharacter.skills || []);
      setCharNote(myCharacter.note || "");
      setCharStats(Array.isArray(myCharacter.stats) ? myCharacter.stats : []);
      setAccent(myCharacter.color || nextColor());
      setCharAC(myCharacter.ac ?? 10);
      setCharHP(myCharacter.hp ?? 8);
      setCharSpeed(myCharacter.speed ?? 30);
      setCharXP(myCharacter.xp ?? 0);
      if (myCharacter.avatarCrop) {
        setAvatarZoom(myCharacter.avatarCrop.zoom ?? 1);
        setAvatarOffsetX(myCharacter.avatarCrop.offsetX ?? 0.5);
        setAvatarOffsetY(myCharacter.avatarCrop.offsetY ?? 0.5);
      }
      if (savedAvatar && savedAvatar !== charAvatar) {
        setCharAvatar(savedAvatar);
      }
      setEditingCharacterId(null);
    }
  }, [myCharacter, editingCharacterId, savedAvatar, charAvatar]);

  // Keep avatar in sync with saved character when not actively cropping
  useEffect(() => {
    if (!avatarDraft && savedAvatar && savedAvatar !== charAvatar) {
      setCharAvatar(savedAvatar);
    }
  }, [avatarDraft, savedAvatar, charAvatar]);

  const partyLocal = useMemo(() => {
    if (!table?.cells) return [];
    return table.cells.filter((c) => hexDistance(c, partyPosition) <= 2);
  }, [table, partyPosition]);

  // Encounter map: load from table if present
  useEffect(() => {
    if (!table) {
      setEncounterMap(null);
      return;
    }
    setEncounterMap(table.encounterMap || null);
  }, [table]);

  // Group chat (table-scoped)
  useEffect(() => {
    if (!chatCol) return;
    const qy = query(chatCol, orderBy("createdAt", "desc"), limit(50));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null };
        });
        setChatMessages(rows);
      },
      (err) => console.error("[chat]", err)
    );
    return () => unsub();
  }, [chatCol]);

  const sendChat = async () => {
    if (!chatInput.trim() || !chatCol) return;
    try {
      await ensureChatAuth();
    } catch (err) {
      setStatus("Sign in to chat with the group.");
      return;
    }
    try {
      await addDoc(chatCol, {
        text: chatInput.trim(),
        by: user?.displayName || "Traveler",
        uid: user?.uid || "anon",
        createdAt: serverTimestamp(),
      });
      setChatInput("");
    } catch (err) {
      console.error("[chat send]", err);
      setStatus(`Chat failed: ${err.message || err.toString()}`);
    }
  };

  // Seed a simple square encounter map and save to table
  const seedEncounterMap = async () => {
    if (!activeTableId || !user) return;
    setMapLoading(true);
    try {
      const width = 12;
      const height = 12;
      const terrains = ["grass", "dirt", "stone"];
      const cells = [];
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const key = `${x},${y}`;
          const edge = x === 0 || y === 0 || x === width - 1 || y === height - 1;
          cells.push({
            id: key,
            x,
            y,
            terrain: edge ? "tree" : terrains[Math.floor(Math.random() * terrains.length)],
            blocked: edge
          });
        }
      }
      // place seated characters near center
      const positions = {};
      const center = { x: Math.floor(width / 2), y: Math.floor(height / 2) };
      (table?.seated || []).forEach((s, idx) => {
        const px = center.x + (idx % 3) - 1;
        const py = center.y + Math.floor(idx / 3) - 1;
        positions[s.ownerId] = { x: Math.max(1, Math.min(width - 2, px)), y: Math.max(1, Math.min(height - 2, py)) };
      });

      await runTransaction(db, async (tx) => {
        const ref = doc(db, "oddventure_tables", activeTableId);
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error("Table missing");
        tx.update(ref, {
          encounterMap: {
            width,
            height,
            cells,
            positions,
            updatedAt: Date.now()
          }
        });
      });
      setEncounterMap({ width, height, cells, positions, updatedAt: Date.now() });
      setStatus("Encounter map seeded.");
    } catch (err) {
      console.error("[seedEncounterMap]", err);
      setStatus("Could not seed map.");
    } finally {
      setMapLoading(false);
    }
  };

  const mapBounds = useMemo(() => {
    const cells = table?.cells || [];
    if (!cells.length) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    const coords = cells.map((c) => axialToPixel(c.q, c.r));
    return {
      minX: Math.min(...coords.map((c) => c.x)) - HEX_W * 0.5,
      maxX: Math.max(...coords.map((c) => c.x)) + HEX_W * 0.5,
      minY: Math.min(...coords.map((c) => c.y)) - HEX_H * 0.5,
      maxY: Math.max(...coords.map((c) => c.y)) + HEX_H * 0.5,
    };
  }, [table]);
  const characterValid = useMemo(() => {
    const limit = getMaxSkillsForClass(charClass);
    return Boolean(
      activeTableId &&
      charName.trim() &&
      charStats.length === 6 &&
      charSkills.length === limit
    );
  }, [activeTableId, charName, charStats, charSkills, charClass]);

  const heroStyle = {
    background: "radial-gradient(circle at 18% 20%, rgba(255,255,255,0.12), transparent 45%), radial-gradient(circle at 78% 40%, rgba(129,240,195,0.18), transparent 35%), linear-gradient(145deg, #0f0f1c, #0c1a25 38%, #142832)",
    color: "#e7f7ff",
    padding: "48px 20px 28px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  };

  if (isDetail && loading) {
    return <div style={{ color: "#e3f2ff", background: "#0c1119", minHeight: "100vh", padding: 32 }}>Loading Chadventure table…</div>;
  }

  if (isDetail && !loading && !table) {
    return <div style={{ color: "#e3f2ff", background: "#0c1119", minHeight: "100vh", padding: 32 }}>No table found for that ID.</div>;
  }

  return (
    <div style={{ color: "#e3f2ff", background: "#0c1119", minHeight: "100vh", fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif" }}>
      {!isDetail && (
        <div style={heroStyle}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <div style={{ textTransform: "uppercase", letterSpacing: 4, fontSize: 12, opacity: 0.7 }}>Chadventure</div>
            <h1 style={{ margin: "6px 0 12px", fontSize: 36, letterSpacing: 0.5 }}>A table-ready, DM-guided hex crawl</h1>
            <p style={{ maxWidth: 720, lineHeight: 1.6, color: "#c7d8eb" }}>
              Invite friends, forge characters under classic D&D vibes, and uncover a living hex map together.
              This table is DM’ed by the Oddmaster (that’s me): I’ll surface cues, flavor, and travel notes as you move.
            </p>
            <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
              <button onClick={createTable} style={primaryButton}>Start a fresh Chadventure table</button>
              <div style={{ display: "flex", gap: 8, alignItems: "center", background: "rgba(255,255,255,0.06)", padding: "8px 10px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)" }}>
                <input
                  value={tableIdInput}
                  onChange={(e) => setTableIdInput(e.target.value)}
                  placeholder="Table ID..."
                  style={inputStyle}
                />
                <button onClick={joinTableById} style={ghostButton}>Join</button>
              </div>
              {activeTableId && (
                <Link to={`/oddventure/${activeTableId}`} style={{ color: "#81f0c3", textDecoration: "underline" }}>
                  Share Chadventure link → /oddventure/{activeTableId}
                </Link>
              )}
            </div>
            {status && <div style={{ marginTop: 10, color: "#9ef7ff", fontSize: 14 }}>{status}</div>}
          </div>
        </div>
      )}

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 20px 48px", display: "grid", gap: 18 }}>
        {!isDetail && (
          <Box tone="default" title="My tables">
            <MyOddventureGames headingLevel={3} layout="stack" style={{ marginTop: 0 }} />
          </Box>
        )}

        {!isDetail && !activeTableId && (
          <Box tone="frost" title="How it works">
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6, color: "#c7d8eb" }}>
              <li>Create a table or paste an existing ID.</li>
              <li>Forge a character: roll abilities, pick a class, choose trained skills.</li>
              <li>Join the table and move one hex at a time. The Oddmaster reveals flavor as you go.</li>
            </ul>
          </Box>
        )}

        {activeTableId && isDetail && table && showMaps && (
          <Box tone="frost">
            <div style={{ display: "grid", gridTemplateColumns: "0.7fr 0.3fr", gap: 12, alignItems: "start" }}>
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 700 }}>Encounter grid</div>
                    <div style={{ color: "#9fb3c6", fontSize: 12 }}>Squares fit 5e battle/dungeon norms</div>
                  </div>
                </div>
                <SquareBoard
                  cells={partyLocal}
                  discovered={discovered}
                  partyPosition={partyPosition}
                  onStep={moveParty}
                />
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ fontWeight: 700 }}>Overland hexes</div>
                <HexBoard
                  cells={table.cells || []}
                  discovered={discovered}
                  partyPosition={partyPosition}
                  onStep={moveParty}
                  bounds={mapBounds}
                />
              </div>
            </div>
          </Box>
        )}

        {table && (
          <Box tone="moss" title="Party roster">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {(table.characters || []).length === 0 && <div style={{ color: "#9fb3c6" }}>No characters yet. Forge one!</div>}
              {(table.characters || []).map((c) => (
                <div key={c.id} style={{
                  border: `1px solid ${c.color || "rgba(255,255,255,0.1)"}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  background: "rgba(0,0,0,0.35)",
                  display: "grid",
                  gap: 6,
                  minWidth: 200,
                  maxWidth: 240
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {c.avatarUrl ? (
                      <img src={c.avatarUrl} alt="avatar" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(255,255,255,0.2)" }} />
                    ) : (
                      <div style={{ width: 10, height: 10, borderRadius: 999, background: c.color || "#81f0c3" }} />
                    )}
                    <div>
                      <div style={{ fontWeight: 700, color: c.color || "#fff" }}>{c.name}</div>
                      <div style={{ fontSize: 12, color: "#c7d8eb" }}>{c.className} · HP {c.hp}</div>
                    </div>
                  </div>
                  <div style={{ color: "#9fb3c6", fontSize: 12 }}>By {c.ownerName || "Traveler"}</div>
                  <button onClick={() => sitAtTable(c.id)} style={{ ...ghostButton, padding: "6px 10px", fontSize: 12 }}>Play as</button>
                </div>
              ))}
            </div>
          </Box>
        )}

        {table && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, alignItems: "stretch" }}>
            <div style={{ height: "100%" }}>
              <Box tone="ember">
                <div style={{ display: "grid", gap: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: 0.5 }}>Character sheet</div>
                    {!isEditing && myCharacter && (
                      <button onClick={beginEdit} style={{ ...ghostButton, padding: "6px 8px", display: "flex", alignItems: "center", gap: 6 }}>
                        ✎
                      </button>
                    )}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <div
                      onClick={handleAvatarClick}
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        border: `2px solid ${avatarDisplay ? "rgba(255,255,255,0.25)" : "#81f0c3"}`,
                        background: avatarDisplay
                          ? `url(${avatarDisplay}) center/cover no-repeat`
                          : "rgba(0,0,0,0.3)",
                        overflow: "hidden",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundPosition: `${avatarOffsetX * 100}% ${avatarOffsetY * 100}%`,
                        backgroundSize: `${avatarZoom * 100}%`,
                      }}
                      title="Click to upload avatar"
                    >
                      {!avatarDisplay && <span style={{ color: "#9fb3c6", fontSize: 11 }}>Set avatar</span>}
                    </div>
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => handleAvatarUpload(e.target.files?.[0])}
                      disabled={avatarUploading}
                    />
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <label style={labelStyle}>Name</label>
                      <input style={inputStyle} value={charName} onChange={(e) => setCharName(e.target.value)} placeholder="Lyra Quickstep" />
                      <div style={{ marginTop: 6, color: "#9fb3c6", fontSize: 12 }}>
                        Level {levelFromXp(charXP).level}{levelFromXp(charXP).nextXp ? ` — next at ${levelFromXp(charXP).nextXp}` : ""}
                      </div>
                    </div>
                  </div>
                  {avatarUploading && <div style={{ color: "#9fb3c6", fontSize: 12 }}>Uploading avatar…</div>}
                  {avatarDraft && (
                    <div style={{ display: "grid", gap: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 10 }}>
                      <div style={{ fontWeight: 600, color: "#c7d8eb" }}>Adjust avatar crop</div>
                      <div style={{
                        width: "100%",
                        maxWidth: 220,
                        aspectRatio: "1 / 1",
                        borderRadius: 12,
                        border: "1px solid rgba(255,255,255,0.12)",
                        overflow: "hidden",
                        backgroundImage: `url(${avatarDraft.url})`,
                        backgroundSize: `${avatarZoom * 100}%`,
                        backgroundPosition: `${avatarOffsetX * 100}% ${avatarOffsetY * 100}%`,
                        backgroundRepeat: "no-repeat",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
                      }} />
                      <div style={{ display: "grid", gap: 6 }}>
                        <label style={{ ...labelStyle, marginTop: 4 }}>Zoom</label>
                        <input
                          type="range"
                          min="1"
                          max="3"
                          step="0.05"
                          value={avatarZoom}
                          onChange={(e) => setAvatarZoom(Number(e.target.value))}
                        />
                      </div>
                      <div style={{ display: "grid", gap: 6 }}>
                        <label style={{ ...labelStyle, marginTop: 4 }}>Position</label>
                        <div style={{ display: "grid", gap: 8 }}>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <span style={{ color: "#9fb3c6", fontSize: 12 }}>X</span>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={avatarOffsetX}
                              onChange={(e) => setAvatarOffsetX(Number(e.target.value))}
                            />
                          </div>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <span style={{ color: "#9fb3c6", fontSize: 12 }}>Y</span>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={avatarOffsetY}
                              onChange={(e) => setAvatarOffsetY(Number(e.target.value))}
                            />
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button onClick={saveAvatarCrop} style={{ ...primaryButton, padding: "8px 12px" }} disabled={avatarUploading}>
                          Save avatar
                        </button>
                        <button onClick={() => setAvatarDraft(null)} style={{ ...ghostButton, padding: "8px 12px" }} disabled={avatarUploading}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr", alignItems: "start" }}>
                  <div style={{ display: "grid", gap: 8 }}>
                    <label style={labelStyle}>Class</label>
                    {isEditing ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {CLASS_OPTIONS.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => { setCharClass(c.id); setCharSkills([]); }}
                            style={{
                              ...toggleStyle,
                              borderColor: charClass === c.id ? "#ffb56b" : "rgba(255,255,255,0.08)",
                              background: charClass === c.id ? "rgba(255,181,107,0.14)" : "rgba(0,0,0,0.2)",
                            }}
                          >
                            {c.name} d{c.hitDie}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <div style={{ ...ghostButton, background: "rgba(255,181,107,0.14)", borderColor: "#ffb56b" }}>
                          {classMeta?.name || "Adventurer"} d{classMeta?.hitDie || "?"}
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ display: "grid", gap: 6 }}>
                    <label style={labelStyle}>Trained skills ({getMaxSkillsForClass(charClass)} total)</label>
                    {isEditing ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {CLASS_OPTIONS.find((c) => c.id === charClass)?.skills.map((skill) => {
                          const picked = charSkills.includes(skill);
                          const disabled = !picked && charSkills.length >= getMaxSkillsForClass(charClass);
                          return (
                            <button
                              key={skill}
                              onClick={() => toggleSkill(skill)}
                              disabled={disabled}
                              style={{
                                ...toggleStyle,
                                opacity: disabled ? 0.5 : 1,
                                borderColor: picked ? "#81f0c3" : "rgba(255,255,255,0.08)",
                                background: picked ? "rgba(129,240,195,0.16)" : "rgba(0,0,0,0.2)",
                              }}
                            >
                              {skill}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {charSkills.length === 0 && <div style={{ color: "#9fb3c6", fontSize: 13 }}>No skills selected</div>}
                        {charSkills.map((skill) => (
                          <div key={skill} style={{ ...ghostButton, padding: "6px 10px", background: "rgba(129,240,195,0.12)", borderColor: "#81f0c3" }}>
                            {skill}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                  {isEditing && (
                    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                      <button
                        onClick={rollAbilities}
                        style={{
                          ...primaryButton,
                          background: "linear-gradient(90deg, #81f0c3, #9ef7ff)",
                          color: "#0b1a1f",
                          boxShadow: "0 10px 22px rgba(129,240,195,0.35)",
                        }}
                      >
                        Roll abilities
                      </button>
                      {charStats.length > 0 ? (
                        <span style={{ color: "#c7d8eb" }}>{charStats.join(" / ")}</span>
                      ) : (
                        <span style={{ color: "#ffb56b", fontWeight: 600 }}>Roll before saving your hero.</span>
                      )}
                    </div>
                  )}

                  <div style={{ display: "grid", gap: 8 }}>
                    <label style={labelStyle}>Stats</label>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "8px 6px", alignItems: "end", width: "100%" }}>
                      <div style={{ minWidth: 0 }}>
                        <label style={labelStyle}>XP</label>
                        <input
                          style={{ ...inputStyle, width: "100%", minWidth: 0, boxSizing: "border-box" }}
                          type="number"
                          min="0"
                          value={charXP}
                          onChange={(e) => setCharXP(Number(e.target.value) || 0)}
                        />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <label style={labelStyle}>HP</label>
                        <input style={{ ...inputStyle, width: "100%", minWidth: 0, boxSizing: "border-box" }} type="number" value={charHP} onChange={(e) => setCharHP(Number(e.target.value) || 0)} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <label style={labelStyle}>AC</label>
                        <input style={{ ...inputStyle, width: "100%", minWidth: 0, boxSizing: "border-box" }} type="number" value={charAC} onChange={(e) => setCharAC(Number(e.target.value) || 0)} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <label style={labelStyle}>Speed</label>
                        <input style={{ ...inputStyle, width: "100%", minWidth: 0, boxSizing: "border-box" }} type="number" value={charSpeed} onChange={(e) => setCharSpeed(Number(e.target.value) || 0)} />
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "grid", gap: 8 }}>
                    <label style={labelStyle}>Notes</label>
                    <textarea style={{ ...inputStyle, minHeight: 68 }} value={charNote} onChange={(e) => setCharNote(e.target.value)} placeholder="Bonds, quirks, spellbook, gear..." />
                  </div>

                  <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <label style={labelStyle}>Accent</label>
                      <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} style={{ width: 60, height: 34, border: "none", background: "transparent" }} />
                    </div>
                    <button
                      onClick={forgeCharacter}
                      style={{ ...primaryButton, opacity: characterValid ? 1 : 0.4, cursor: characterValid ? "pointer" : "not-allowed" }}
                      disabled={!characterValid}
                    >
                      {editingCharacterId ? "Save changes" : "Save"}
                    </button>
                  </div>
                </div>
              </Box>
            </div>

            <div style={{ height: "100%" }}>
              <Box tone="ember" title="Chat with The Chadmaster">
                <div style={{ display: "grid", gap: 10, gridTemplateRows: "auto auto 1fr", height: "100%" }}>
                  <textarea
                    value={dmInput}
                    onChange={(e) => setDmInput(e.target.value)}
                    placeholder="Describe what you do or ask for a scene..."
                    style={{ ...inputStyle, minHeight: 120, color: "#e3f2ff", maxWidth: "100%", resize: "vertical" }}
                  />
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <button onClick={askOddmaster} style={primaryButton} disabled={dmLoading || !dmInput.trim()}>
                      {dmLoading ? "Asking…" : "Chat with The Chadmaster"}
                    </button>
                    {dmReply && <span style={{ color: "#9fb3c6", fontSize: 13 }}>Last reply: {dmReply}</span>}
                  </div>
                  <div style={{ display: "grid", gap: 6, overflowY: "auto", paddingRight: 4 }}>
                    {dmMessages.length === 0 && <div style={{ color: "#9fb3c6" }}>No chat yet.</div>}
                    {dmMessages.map((entry) => (
                      <div key={entry.id} style={{ padding: "8px 10px", background: "rgba(255,255,255,0.04)", borderRadius: 8, border: "1px solid rgba(255,255,255,0.06)" }}>
                        <div style={{ color: "#9fb3c6", fontSize: 12 }}>{entry.createdAt ? entry.createdAt.toLocaleTimeString() : "…"}</div>
                        <div style={{ color: "#e3f2ff" }}>{entry.text}</div>
                        <div style={{ color: "#9fb3c6", fontSize: 12 }}>— {entry.role === "assistant" ? "Chadmaster" : (entry.by || "Traveler")}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Box>
            </div>

            <div style={{ display: "grid", gap: 16, alignItems: "start", alignContent: "start" }}>
              <Box tone="frost" title="Encounter map">
                <div style={{ display: "grid", gap: 10 }}>
                  {!encounterMap && (
                    <button onClick={seedEncounterMap} style={primaryButton} disabled={mapLoading || !user}>
                      {mapLoading ? "Seeding..." : "Create encounter map"}
                    </button>
                  )}
                  {encounterMap && (
                    <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: 8, background: "rgba(0,0,0,0.25)" }}>
                      <div style={{ display: "grid", gridTemplateColumns: `repeat(${encounterMap.width}, 26px)`, gap: 2 }}>
                        {encounterMap.cells.map((cell) => {
                          const occ = Object.entries(encounterMap.positions || {}).find(([ownerId, pos]) => pos.x === cell.x && pos.y === cell.y);
                          const terrainColor = cell.terrain === "tree" ? "rgba(34,139,34,0.5)" : cell.terrain === "stone" ? "rgba(180,180,180,0.35)" : "rgba(90,150,90,0.35)";
                          return (
                            <div key={cell.id} style={{
                              width: 26,
                              height: 26,
                              background: terrainColor,
                              border: "1px solid rgba(255,255,255,0.08)",
                              position: "relative",
                              boxSizing: "border-box",
                              opacity: cell.blocked ? 0.6 : 1
                            }}>
                              {occ && (
                                <div style={{
                                  position: "absolute",
                                  inset: 2,
                                  borderRadius: 6,
                                  background: (table?.characters || []).find(c => c.ownerId === occ[0])?.color || "#81f0c3",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 10,
                                  color: "#0c1119",
                                  fontWeight: 700
                                }}>
                                  {(table?.characters || []).find(c => c.ownerId === occ[0])?.name?.[0] || "P"}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </Box>

              <Box tone="frost" title="Roll dice">
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <input
                      value={rollExpr}
                      onChange={(e) => setRollExpr(e.target.value)}
                      placeholder="1d20+3"
                      style={{ ...inputStyle, flex: 1, minWidth: 140 }}
                    />
                    <button onClick={() => rollDiceForTable()} style={primaryButton} disabled={rolling || !rollExpr.trim()}>
                      {rolling ? "Rolling…" : "Roll"}
                    </button>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {["1d20", "2d6", "1d12", "1d10+2", "1d8", "1d4"].map((preset) => (
                      <button
                        key={preset}
                        onClick={() => { setRollExpr(preset); rollDiceForTable(preset); }}
                        style={{ ...ghostButton, padding: "6px 10px" }}
                        disabled={rolling}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                </div>
              </Box>

              <Box tone="frost" title="Group chat (log)">
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ display: "grid", gap: 6, maxHeight: 320, overflowY: "auto", paddingRight: 4 }}>
                    {chatMessages.length === 0 && <div style={{ color: "#9fb3c6" }}>No chat yet.</div>}
                    {chatMessages.map((entry) => (
                      <div key={entry.id} style={{ padding: "8px 10px", background: "rgba(255,255,255,0.04)", borderRadius: 8, border: "1px solid rgba(255,255,255,0.06)" }}>
                        <div style={{ color: "#9fb3c6", fontSize: 12 }}>{entry.createdAt ? entry.createdAt.toLocaleTimeString() : "…"}</div>
                        <div style={{ color: "#e3f2ff" }}>{entry.text}</div>
                        <div style={{ color: "#9fb3c6", fontSize: 12 }}>— {entry.by}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder="Say something to the group..."
                      style={{ ...inputStyle, flex: 1 }}
                      onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }}
                    />
                    <button onClick={sendChat} style={primaryButton} disabled={!chatInput.trim()}>Send</button>
                  </div>
                </div>
              </Box>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function HexBoard({ cells, discovered, partyPosition, onStep, allowUndiscovered = false, bounds }) {
  const canvasRef = React.useRef(null);
  const hitTest = React.useRef([]);
  const size = { width: 280, height: 300 };
  const cellSize = 22;

  useEffect(() => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, size.width, size.height);
    ctx.fillStyle = "#0b0f17";
    ctx.fillRect(0, 0, size.width, size.height);
    hitTest.current = [];

    const coords = cells.map((c) => {
      const x = cellSize * 1.5 * c.q;
      const y = cellSize * Math.sqrt(3) * (c.r + c.q / 2);
      return { ...c, x, y };
    });
    if (!coords.length) return;
    const minX = Math.min(...coords.map((c) => c.x));
    const maxX = Math.max(...coords.map((c) => c.x));
    const minY = Math.min(...coords.map((c) => c.y));
    const maxY = Math.max(...coords.map((c) => c.y));
    const offsetX = (size.width - (maxX - minX)) / 2;
    const offsetY = (size.height - (maxY - minY)) / 2;

    coords.forEach((cell) => {
      const cx = cell.x - minX + offsetX;
      const cy = cell.y - minY + offsetY;
      const isParty = cell.q === partyPosition.q && cell.r === partyPosition.r;
      const isDiscovered = discovered.has(cell.id);
      const canMove = (allowUndiscovered || isDiscovered) && hexDistance(cell, partyPosition) === 1;
      const foggy = !isDiscovered;

      const points = [];
      for (let i = 0; i < 6; i++) {
        const angle = Math.PI / 3 * i + Math.PI / 6;
        points.push({
          x: cx + cellSize * Math.cos(angle),
          y: cy + cellSize * Math.sin(angle),
        });
      }

      ctx.beginPath();
      points.forEach((p, idx) => {
        if (idx === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
      });
      ctx.closePath();
      const grad = ctx.createLinearGradient(cx - cellSize, cy - cellSize, cx + cellSize, cy + cellSize);
      grad.addColorStop(0, foggy ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.08)");
      grad.addColorStop(1, foggy ? "rgba(0,0,0,0.65)" : "rgba(0,0,0,0.35)");
      ctx.fillStyle = foggy ? grad : biomeColor(cell.biome);
      ctx.fill();
      ctx.lineWidth = isParty ? 3 : canMove ? 1.2 : 1;
      ctx.strokeStyle = isParty ? "#ffeb3b" : "rgba(255,255,255,0.35)";
      ctx.stroke();

      hitTest.current.push({ id: cell.id, cx, cy, radius: cellSize * 0.9, canMove });
    });
  }, [cells, discovered, partyPosition, onStep, allowUndiscovered, size.height, size.width]);

  const handleClick = (evt) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = evt.clientX - rect.left;
    const y = evt.clientY - rect.top;
    const target = hitTest.current.find((h) => {
      const dx = x - h.cx;
      const dy = y - h.cy;
      return dx * dx + dy * dy <= h.radius * h.radius;
    });
    if (target && target.canMove) onStep?.(target.id);
  };

  return (
    <canvas
      ref={canvasRef}
      width={size.width}
      height={size.height}
      onClick={handleClick}
      style={{
        width: size.width,
        height: size.height,
        borderRadius: 18,
        background: "#0b0f17",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
      }}
    />
  );
}

function SquareBoard({ cells, discovered, partyPosition, onStep }) {
  const canvasRef = React.useRef(null);
  const hitTest = React.useRef([]);
  const size = { width: 520, height: 360 };
  const cellSize = 44;

  useEffect(() => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, size.width, size.height);
    ctx.fillStyle = "#0b0f17";
    ctx.fillRect(0, 0, size.width, size.height);
    hitTest.current = [];

    if (!cells.length) return;
    const minQ = Math.min(...cells.map((c) => c.q));
    const maxQ = Math.max(...cells.map((c) => c.q));
    const minR = Math.min(...cells.map((c) => c.r));
    const maxR = Math.max(...cells.map((c) => c.r));
    const cols = maxQ - minQ + 3;
    const rows = maxR - minR + 3;
    const startX = (size.width - cols * cellSize) / 2;
    const startY = (size.height - rows * cellSize) / 2;

    cells.forEach((cell) => {
      const col = cell.q - minQ + 1;
      const row = cell.r - minR + 1;
      if (col < 0 || col >= cols || row < 0 || row >= rows) return;
      const x = startX + col * cellSize;
      const y = startY + row * cellSize;
      const isParty = cell.q === partyPosition.q && cell.r === partyPosition.r;
      const isDiscovered = discovered.has(cell.id);
      const canMove = isDiscovered && hexDistance(cell, partyPosition) === 1;

      ctx.fillStyle = isDiscovered ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.6)";
      ctx.strokeStyle = isParty ? "#ffeb3b" : canMove ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.08)";
      ctx.lineWidth = isParty ? 3 : 1;
      ctx.beginPath();
      ctx.rect(x, y, cellSize, cellSize);
      ctx.fill();
      ctx.stroke();

      hitTest.current.push({ id: cell.id, x, y, w: cellSize, h: cellSize, canMove });
    });
  }, [cells, discovered, partyPosition, onStep, size.height, size.width]);

  const handleClick = (evt) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = evt.clientX - rect.left;
    const y = evt.clientY - rect.top;
    const target = hitTest.current.find((h) => x >= h.x && x <= h.x + h.w && y >= h.y && y <= h.y + h.h);
    if (target && target.canMove) onStep?.(target.id);
  };

  return (
    <canvas
      ref={canvasRef}
      width={size.width}
      height={size.height}
      onClick={handleClick}
      style={{
        width: size.width,
        height: size.height,
        borderRadius: 18,
        background: "#0b0f17",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
      }}
    />
  );
}

function biomeColor(biome) {
  const map = {
    "Blazing Steppe": "linear-gradient(145deg, #c45b2c, #62230e)",
    "Mistwood": "linear-gradient(145deg, #2f6b4a, #0c1f15)",
    "Glass Marsh": "linear-gradient(145deg, #3d6f82, #0f1d2c)",
    "Thunder Mesa": "linear-gradient(145deg, #2c2f62, #0d0f23)",
    "Echoing Chasm": "linear-gradient(145deg, #1f2f48, #0a0d19)",
    "Aurora Tundra": "linear-gradient(145deg, #4a7ca6, #14263a)",
    "Sunken Archive": "linear-gradient(145deg, #4a3b70, #171027)",
  };
  return map[biome] || "linear-gradient(145deg, #2b2b2b, #0f0f0f)";
}

const inputStyle = {
  background: "rgba(0,0,0,0.3)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 10,
  padding: "8px 10px",
  color: "#e3f2ff",
  minWidth: 0,
};

const primaryButton = {
  background: "linear-gradient(90deg, #ff8a5b, #ffb56b)",
  border: "none",
  color: "#0f0f0f",
  fontWeight: 700,
  padding: "10px 14px",
  borderRadius: 12,
  cursor: "pointer",
  boxShadow: "0 12px 24px rgba(0,0,0,0.35)",
};

const ghostButton = {
  background: "rgba(255,255,255,0.08)",
  border: "1px solid rgba(255,255,255,0.18)",
  color: "#e3f2ff",
  padding: "8px 12px",
  borderRadius: 10,
  cursor: "pointer",
};

const labelStyle = {
  textTransform: "uppercase",
  letterSpacing: 1,
  fontSize: 12,
  color: "#9fb3c6",
};

const toggleStyle = {
  background: "rgba(0,0,0,0.25)",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "#e3f2ff",
  padding: "8px 10px",
  borderRadius: 10,
  cursor: "pointer",
};
                    <div style={{ color: "#9fb3c6", fontSize: 12 }}>
                      Note: image stays original; crop/position are applied on display.
                    </div>
